import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch } from '../../lib/api';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './ArchivioBanchePage.api';
import './ArchivioBanchePage.css';

// ── Tipi ────────────────────────────────────────────────────────────────────
interface Banca { abi: string; denominazione: string; }
interface Sportello { abi: string; cab: string; sportello: string; citta?: string | null; cap?: string | null; provincia?: string | null; }
interface Stats { nBanche: number; nSportelli: number; ultimoAggiornamento: string | null; }

// Formattazione intero con separatore migliaia (non è un valore monetario)
const fmtInt = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

export default function ArchivioBanchePage() {
  const { can } = usePermesso();
  const canManage = can('amministrazione.anagrafica_banche', 'edit');

  const [tab, setTab] = useState<'banche' | 'sportelli'>('banche');
  const [stats, setStats] = useState<Stats | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Banche
  const [banche, setBanche] = useState<Banca[]>([]);
  const [qBanche, setQBanche] = useState('');
  const [loadingBanche, setLoadingBanche] = useState(false);

  // Sportelli
  const [sportelli, setSportelli] = useState<Sportello[]>([]);
  const [qSportelli, setQSportelli] = useState('');
  const [loadingSportelli, setLoadingSportelli] = useState(false);

  // Aggiornamento archivio
  const [aggiornando, setAggiornando] = useState(false);

  // Modale banca
  const [modal, setModal] = useState<{ abi: string; denominazione: string; isNew: boolean } | null>(null);
  const [saving, setSaving] = useState(false);

  const mostraToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const loadStats = useCallback(async () => {
    try {
      // @api GET /api/anagrafica-banche/stats -> {"success":"boolean","data":{"nBanche":"number","nSportelli":"number","ultimoAggiornamento":"string"}}
      const res = await apiFetch<{ success: boolean; data: Stats }>('/api/anagrafica-banche/stats');
      setStats(res.data);
    } catch { /* ignore */ }
  }, []);

  const loadBanche = useCallback(async (q: string) => {
    setLoadingBanche(true);
    try {
      // @api GET /api/anagrafica-banche?q= -> {"success":"boolean","data":"Banca[]"}
      const res = await apiFetch<{ success: boolean; data: Banca[] }>(`/api/anagrafica-banche${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      setBanche(res.data || []);
    } catch {
      setBanche([]);
    } finally {
      setLoadingBanche(false);
    }
  }, []);

  const loadSportelli = useCallback(async (q: string) => {
    if (!q.trim()) { setSportelli([]); return; }
    setLoadingSportelli(true);
    try {
      // @api GET /api/anagrafica-banche/sportelli?q=&limit= -> {"success":"boolean","data":"Sportello[]"}
      const res = await apiFetch<{ success: boolean; data: Sportello[] }>(`/api/anagrafica-banche/sportelli?q=${encodeURIComponent(q)}&limit=50`);
      setSportelli(res.data || []);
    } catch {
      setSportelli([]);
    } finally {
      setLoadingSportelli(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Debounce ricerca banche
  useEffect(() => {
    const t = setTimeout(() => loadBanche(qBanche), 200);
    return () => clearTimeout(t);
  }, [qBanche, loadBanche]);

  // Debounce ricerca sportelli
  useEffect(() => {
    const t = setTimeout(() => loadSportelli(qSportelli), 250);
    return () => clearTimeout(t);
  }, [qSportelli, loadSportelli]);

  const aggiornaArchivio = async () => {
    if (!window.confirm('Riscaricare l\'archivio nazionale banche/sportelli dalla fonte pubblica? L\'operazione aggiorna i record esistenti e aggiunge i nuovi.')) return;
    setAggiornando(true);
    try {
      // @api POST /api/anagrafica-banche/aggiorna -> {"success":"boolean","data":{"banche":"number","sportelli":"number"}}
      const res = await apiFetch<{ success: boolean; data: { banche: number; sportelli: number } }>('/api/anagrafica-banche/aggiorna', { method: 'POST' });
      mostraToast('success', `Archivio aggiornato: ${fmtInt(res.data.banche)} banche, ${fmtInt(res.data.sportelli)} sportelli.`);
      await loadStats();
      await loadBanche(qBanche);
    } catch (err: any) {
      mostraToast('error', err?.message || 'Aggiornamento fallito');
    } finally {
      setAggiornando(false);
    }
  };

  const saveBanca = async () => {
    if (!modal) return;
    const abi = modal.abi.trim();
    const denominazione = modal.denominazione.trim();
    if (!/^\d{5}$/.test(abi)) { mostraToast('error', 'ABI non valido (5 cifre)'); return; }
    if (!denominazione) { mostraToast('error', 'Denominazione obbligatoria'); return; }
    setSaving(true);
    try {
      if (modal.isNew) {
        // @api POST /api/anagrafica-banche/banca {"abi":"string","denominazione":"string"} -> {"success":"boolean","data":"Banca"}
        await apiFetch('/api/anagrafica-banche/banca', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ abi, denominazione }),
        });
      } else {
        // @api PUT /api/anagrafica-banche/banca/:abi {"denominazione":"string"} -> {"success":"boolean"}
        await apiFetch(`/api/anagrafica-banche/banca/${abi}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ denominazione }),
        });
      }
      setModal(null);
      mostraToast('success', 'Banca salvata.');
      await loadBanche(qBanche);
      await loadStats();
    } catch (err: any) {
      mostraToast('error', err?.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const deleteBanca = async (b: Banca) => {
    if (!window.confirm(`Eliminare la banca ABI ${b.abi} (${b.denominazione})?`)) return;
    try {
      // @api DELETE /api/anagrafica-banche/banca/:abi -> {"success":"boolean"}
      await apiFetch(`/api/anagrafica-banche/banca/${b.abi}`, { method: 'DELETE' });
      mostraToast('success', 'Banca eliminata.');
      await loadBanche(qBanche);
      await loadStats();
    } catch (err: any) {
      mostraToast('error', err?.message || 'Errore nell\'eliminazione');
    }
  };

  return (
    <Layout>
      <div className="archivio-banche-page">
        {toast && (
          <div className={`amministrazione-toast ${toast.type === 'success' ? 'amministrazione-toast-success' : 'amministrazione-toast-error'}`}>
            {toast.msg}
          </div>
        )}

        <div className="bop-page-header">
          <h1 className="bop-page-title">Archivio banche ABI/CAB</h1>
          {canManage && (
            <button className="bop-btn-primary" onClick={aggiornaArchivio} disabled={aggiornando}>
              {aggiornando ? 'Aggiornamento in corso…' : 'Aggiorna archivio'}
            </button>
          )}
        </div>

        {/* Statistiche */}
        <div className="archivio-banche-stats">
          <div className="archivio-banche-stat">
            <div className="archivio-banche-stat__value">{stats ? fmtInt(stats.nBanche) : '—'}</div>
            <div className="archivio-banche-stat__label">Banche (ABI)</div>
          </div>
          <div className="archivio-banche-stat">
            <div className="archivio-banche-stat__value">{stats ? fmtInt(stats.nSportelli) : '—'}</div>
            <div className="archivio-banche-stat__label">Sportelli (ABI+CAB)</div>
          </div>
          <div className="archivio-banche-stat">
            <div className="archivio-banche-stat__value">
              {stats?.ultimoAggiornamento ? new Date(stats.ultimoAggiornamento).toLocaleString('it-IT') : '—'}
            </div>
            <div className="archivio-banche-stat__label">Ultimo aggiornamento</div>
          </div>
        </div>

        {/* Tab */}
        <div className="bop-tabs">
          <button className={`bop-tab ${tab === 'banche' ? 'active' : ''}`} onClick={() => setTab('banche')}>Banche</button>
          <button className={`bop-tab ${tab === 'sportelli' ? 'active' : ''}`} onClick={() => setTab('sportelli')}>Sportelli</button>
        </div>

        {/* TAB BANCHE */}
        {tab === 'banche' && (
          <div className="bop-card bop-card__body">
            <div className="archivio-banche-toolbar">
              <input
                className="bop-input"
                placeholder="Cerca per ABI o denominazione…"
                value={qBanche}
                onChange={(e) => setQBanche(e.target.value)}
              />
              {canManage && (
                <button className="bop-btn-primary" onClick={() => setModal({ abi: '', denominazione: '', isNew: true })}>
                  + Nuova banca
                </button>
              )}
            </div>

            <table className="bop-table">
              <thead>
                <tr>
                  <th>ABI</th>
                  <th>Denominazione</th>
                  {canManage && <th className="bop-text-right">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {loadingBanche ? (
                  <tr><td colSpan={canManage ? 3 : 2}><div className="bop-empty-state">Caricamento…</div></td></tr>
                ) : banche.length === 0 ? (
                  <tr><td colSpan={canManage ? 3 : 2}><div className="bop-empty-state">Nessuna banca trovata</div></td></tr>
                ) : (
                  banche.map((b) => (
                    <tr key={b.abi}>
                      <td className="bop-text-mono">{b.abi}</td>
                      <td>{b.denominazione}</td>
                      {canManage && (
                        <td className="bop-text-right">
                          <BopRowActions actions={[
                            { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => setModal({ abi: b.abi, denominazione: b.denominazione, isNew: false }) },
                            { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => deleteBanca(b), variant: 'danger' },
                          ]} />
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {banche.length >= 1000 && (
              <div className="bop-text-muted">Mostrati i primi 1000 risultati: affina la ricerca.</div>
            )}
          </div>
        )}

        {/* TAB SPORTELLI */}
        {tab === 'sportelli' && (
          <div className="bop-card bop-card__body">
            <div className="archivio-banche-toolbar">
              <input
                className="bop-input"
                placeholder="Cerca filiale per città, nome o CAB…"
                value={qSportelli}
                onChange={(e) => setQSportelli(e.target.value)}
              />
            </div>

            <table className="bop-table">
              <thead>
                <tr>
                  <th>ABI</th>
                  <th>CAB</th>
                  <th>Filiale</th>
                  <th>Città</th>
                  <th>Prov.</th>
                </tr>
              </thead>
              <tbody>
                {loadingSportelli ? (
                  <tr><td colSpan={5}><div className="bop-empty-state">Caricamento…</div></td></tr>
                ) : sportelli.length === 0 ? (
                  <tr><td colSpan={5}><div className="bop-empty-state">{qSportelli ? 'Nessuno sportello trovato' : 'Digita per cercare uno sportello'}</div></td></tr>
                ) : (
                  sportelli.map((s, i) => (
                    <tr key={`${s.abi}-${s.cab}-${i}`}>
                      <td className="bop-text-mono">{s.abi}</td>
                      <td className="bop-text-mono">{s.cab}</td>
                      <td>{s.sportello}</td>
                      <td>{s.citta || '—'}</td>
                      <td>{s.provincia || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {sportelli.length >= 50 && (
              <div className="bop-text-muted">Mostrati i primi 50 risultati: affina la ricerca.</div>
            )}
          </div>
        )}

        {/* Modale banca */}
        {modal && (
          <div className="bop-modal-overlay" onClick={() => !saving && setModal(null)}>
            <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bop-modal__header">
                <h3 className="bop-modal__title">{modal.isNew ? 'Nuova banca' : `Modifica banca ${modal.abi}`}</h3>
              </div>
              <div className="bop-modal__body">
                <div className="bop-form-group">
                  <label className="bop-label">ABI *</label>
                  <input
                    className="bop-input"
                    type="text"
                    inputMode="numeric"
                    value={modal.abi}
                    onChange={(e) => setModal({ ...modal, abi: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                    placeholder="5 cifre"
                    disabled={saving || !modal.isNew}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Denominazione *</label>
                  <input
                    className="bop-input"
                    type="text"
                    value={modal.denominazione}
                    onChange={(e) => setModal({ ...modal, denominazione: e.target.value })}
                    placeholder="Nome della banca"
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="bop-modal__footer">
                <button className="bop-btn-secondary" onClick={() => setModal(null)} disabled={saving}>Annulla</button>
                <button className="bop-btn-primary" onClick={saveBanca} disabled={saving}>
                  {saving ? 'Salvataggio…' : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
